import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Loader2,
  Pencil,
} from "lucide-react";
import React from "react";
import { useSelector } from "react-redux";
import { DynamicLinkDestinationUrls } from "src/components/dynamicLinks/DynamicLinkDestinationUrls";
import { canManageService, ServiceDomain, UserRole } from "src/lib/roles";
import { selectUserProfile } from "src/store/authSlice";
import type { DynamicLinkListItem } from "src/types/dynamicLinks";

function formatTs(
  t: { toDate?: () => Date; toMillis?: () => number } | null | undefined,
): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try {
    return t.toDate().toLocaleString();
  } catch {
    return "—";
  }
}

export type DynamicLinksListSectionProps = {
  links: DynamicLinkListItem[];
  loading: boolean;
  copiedSlug: string | null;
  expandedSlug: string | null;
  onOpenEdit: (row: DynamicLinkListItem) => void;
  onCopy: (shortUrl: string, slugKey: string) => void;
  onToggleExpandSlug: (slug: string) => void;
};

export const DynamicLinksListSection: React.FC<
  DynamicLinksListSectionProps
> = ({
  links,
  loading,
  copiedSlug,
  expandedSlug,
  onOpenEdit,
  onCopy,
  onToggleExpandSlug,
}) => {
  const userProfile = useSelector(selectUserProfile);
  const role = userProfile?.role ?? UserRole.VIEWER;
  const canStaffManageLinks = canManageService(
    role,
    ServiceDomain.DynamicLinks,
  );
  return (
    <section aria-labelledby="your-links-heading">
      <div className="mb-4 flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
        <h2
          id="your-links-heading"
          className="text-center text-2xl font-extrabold text-white sm:text-left"
        >
          Your links
        </h2>
        {!loading && links.length > 0 && (
          <span className="rounded-full bg-white/15 px-4 py-1.5 text-sm font-extrabold text-white/90 ring-2 ring-white/20">
            {links.length} {links.length === 1 ? "link" : "links"}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center rounded-[1.75rem] border-4 border-white/10 bg-white/5 py-16">
          <Loader2
            className="h-12 w-12 animate-spin text-[#58CC02]"
            aria-label="Loading links"
          />
          <p className="mt-4 font-extrabold text-white/70">
            Loading your links…
          </p>
        </div>
      ) : links.length === 0 ? (
        <div className="rounded-[1.75rem] border-4 border-dashed border-white/25 bg-white/5 p-10 text-center">
          <p className="text-lg font-extrabold text-white/90">
            No links yet — create your first one above!
          </p>
          <p className="mt-2 font-bold text-white/60">
            Short URL + smart routing = fewer confused users.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-4">
          {links.map((row) => {
            const open = expandedSlug === row.slug;
            return (
              <li
                key={row.slug}
                className="overflow-hidden rounded-[1.5rem] border-4 border-[#E5E5E5] bg-white shadow-[0_6px_0_#D0D0D0]"
              >
                <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-5">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="truncate text-lg font-extrabold text-[#3C3C3C]">
                        {row.name || "Untitled link"}
                      </span>
                      <span
                        className={
                          row.status === "active"
                            ? "rounded-lg bg-[#E8F8E0] px-2 py-0.5 text-xs font-extrabold uppercase text-[#3F9600]"
                            : "rounded-lg bg-[#FFF4D4] px-2 py-0.5 text-xs font-extrabold uppercase text-[#A87000]"
                        }
                      >
                        {row.status}
                      </span>
                    </div>
                    <code className="block truncate text-sm font-bold text-[#1CB0F6]">
                      /{row.slug}
                    </code>
                    <p className="mt-2 truncate font-mono text-xs font-semibold text-[#777777]">
                      {row.shortUrl}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-stretch">
                    {canStaffManageLinks && (
                      <button
                        type="button"
                        onClick={() => onOpenEdit(row)}
                        className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border-2 border-[#E5E5E5] bg-white px-4 py-2 text-sm font-extrabold text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0] hover:bg-[#FAFAFA] active:translate-y-0.5 sm:flex-none"
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onCopy(row.shortUrl, row.slug)}
                      className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border-2 border-[#E5E5E5] bg-[#F7F7F7] px-4 py-2 text-sm font-extrabold text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0] transition-colors hover:bg-[#EFEFEF] active:translate-y-0.5 active:shadow-none sm:flex-none"
                    >
                      {copiedSlug === row.slug ? (
                        <>
                          <Check className="h-4 w-4 text-[#58CC02]" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy link
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleExpandSlug(row.slug)}
                      className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1 rounded-xl border-2 border-[#1CB0F6] bg-[#E8F6FE] px-4 py-2 text-sm font-extrabold text-[#1899D6] active:translate-y-0.5 sm:flex-none"
                      aria-expanded={open}
                    >
                      {open ? (
                        <>
                          Hide details
                          <ChevronUp className="h-4 w-4" />
                        </>
                      ) : (
                        <>
                          See targets
                          <ChevronDown className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 border-t-2 border-[#F0F0F0] bg-[#FAFAFA] px-4 py-3 text-xs font-bold text-[#777777] sm:px-5">
                  <span className="rounded-lg bg-white px-2 py-1 ring-1 ring-[#E5E5E5]">
                    Clicks:{" "}
                    <strong className="text-[#3C3C3C]">
                      {typeof row.clickCount === "number" ? row.clickCount : 0}
                    </strong>
                  </span>
                  <span className="rounded-lg bg-white px-2 py-1 ring-1 ring-[#E5E5E5]">
                    Expires: {formatTs(row.expiresAt)}
                  </span>
                  <span className="rounded-lg bg-white px-2 py-1 ring-1 ring-[#E5E5E5]">
                    By: {row.createdByDisplayName?.trim() || row.createdBy}
                  </span>
                </div>

                {open && (
                  <DynamicLinkDestinationUrls
                    androidUrl={row.androidUrl}
                    iosUrl={row.iosUrl}
                    fallbackUrl={row.fallbackUrl}
                    createdAt={row.createdAt}
                    updatedAt={row.updatedAt}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
